import { useState } from 'react';
import type { Schema } from '../api/client';
import { ApiError } from '../api/session';
import { Button, Empty, ErrorState, Field, Icon, Search, Table } from '../ui';
import {
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  InfoRows,
  Missing,
  QueryState,
  dateTimeLabel,
  text,
  useAdminAction,
  useAdminList,
  useAdminNavigate,
  useAdminQuery,
  useAdministrative,
} from './common';
import { AdministrativeList, CompactFilter } from './list-layout';

type Member = Schema<'AdminMember'>;
type Role = Schema<'AdminRole'>;
type Invite = Schema<'AdminInvite'>;
type Rule = Schema<'AdminRoleCreate'>['rules'][number];
const actionLabels: Record<Rule['action'], string> = {
  'resource.read': 'Просмотр записей',
  'resource.create': 'Создание записей',
  'resource.edit': 'Изменение записей',
  'resource.archive': 'Архивирование записей',
  'resource.delete': 'Удаление записей',
  'section.read': 'Просмотр разделов',
  'section.create': 'Создание разделов',
  'section.edit': 'Изменение разделов',
  'section.delete': 'Удаление разделов',
  'sheet.read': 'Просмотр листов',
  'sheet.create': 'Создание листов',
  'sheet.edit': 'Изменение листов',
  'sheet.delete': 'Удаление листов',
  'provider.read': 'Просмотр аккаунтов поставщиков',
  'provider.edit': 'Изменение аккаунтов поставщиков',
  'finance.read': 'Просмотр цен, карт, балансов и оплат',
  'finance.edit': 'Изменение цен, карт, балансов и продление',
  'connection.read': 'Просмотр данных подключения',
  'connection.edit': 'Изменение данных подключения',
  'members.read': 'Просмотр участников и ролей',
  'members.edit': 'Приглашение и изменение участников',
  'roles.edit': 'Изменение ролей и прав',
  'trash.read': 'Просмотр корзины',
  'trash.restore': 'Восстановление из корзины',
  'trash.purge': 'Окончательное удаление',
  'audit.read': 'Просмотр журнала',
  'settings.read': 'Просмотр справочников',
  'settings.edit': 'Изменение справочников и настроек',
  export: 'Экспорт доступных данных',
  import: 'Импорт данных',
  'proxy.check': 'Проверка прокси',
};
const statusLabels: Record<string, string> = {
  ACTIVE: 'Активен',
  PENDING_ACTIVATION: 'Ожидает активации',
  BLOCKED: 'Заблокирован',
  DISABLED: 'Отключён',
};
const scopeLabels: Record<Rule['scope'], string> = {
  WORKSPACE: 'Всё пространство',
  SECTION: 'Раздел',
  SHEET: 'Лист',
  RESOURCE: 'Запись',
};
function allowsOwnRecords(action: string) {
  return (
    action.startsWith('resource.') ||
    action.startsWith('finance.') ||
    action.startsWith('connection.')
  );
}

export function AdministrativeAccess({
  section,
  id,
  operation,
}: {
  section: string;
  id?: string;
  operation?: string;
}) {
  const me = useAdministrative();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const isRoles = section === 'roles';
  const isInvites = section === 'invites';
  const canReadMembers = me.allowedActions.includes('members.read');
  const canEditRoles = me.allowedActions.includes('roles.edit');
  const permitted = isRoles ? canReadMembers || canEditRoles : canReadMembers;
  const needsMembers = !isRoles && !isInvites;
  const needsRoles = !isInvites || id === 'new';
  const needsInvites = isInvites && id !== 'new';
  const members = useAdminQuery<Schema<'AdminMemberPage'>>(
    '/api/v1/administrative/members',
    {},
    {},
    permitted && needsMembers,
  );
  const roles = useAdminQuery<Schema<'AdminRolePage'>>(
    '/api/v1/administrative/roles',
    {},
    {},
    permitted && needsRoles,
  );
  const invites = useAdminQuery<Schema<'AdminInvitePage'>>(
    '/api/v1/administrative/invites',
    {},
    {},
    permitted && needsInvites,
  );
  const navigate = useAdminNavigate();
  if (!permitted)
    return (
      <AdministrativeList>
        <Empty
          title={isRoles ? 'Доступ к ролям не предоставлен' : 'Доступ к участникам не предоставлен'}
        />
      </AdministrativeList>
    );
  const neededQueries = [
    ...(needsMembers ? [members] : []),
    ...(needsRoles ? [roles] : []),
    ...(needsInvites ? [invites] : []),
  ];
  const failed = neededQueries.find((query) => query.error);
  if (failed)
    return (
      <ErrorState
        error={failed.error}
        retry={() => neededQueries.forEach((query) => void query.refetch())}
      />
    );
  const waiting = neededQueries.find((query) => !query.data);
  if (waiting) return <QueryState query={waiting} />;
  if (section === 'invites' && id === 'new') return <InviteForm roles={roles.data!.items} />;
  if (section === 'roles' && (id === 'new' || operation === 'edit')) {
    const role = roles.data!.items.find((r) => r.id === id);
    return id !== 'new' && !role ? (
      <Missing />
    ) : (
      <RoleForm
        key={id}
        role={role}
        reload={async () => {
          const result = await roles.refetch();
          return result.error ? undefined : result.data?.items.find((item) => item.id === id);
        }}
      />
    );
  }
  if (section === 'members' && id) {
    const member = members.data!.items.find((row) => row.id === id);
    return member ? (
      <MemberForm
        key={id}
        member={member}
        roles={roles.data!.items}
        reload={async () => {
          const result = await members.refetch();
          return result.error ? undefined : result.data?.items.find((item) => item.id === id);
        }}
      />
    ) : (
      <Missing />
    );
  }
  const term = search.toLocaleLowerCase('ru');
  const roleRows = roles.data?.items || [];
  const memberRows = (members.data?.items || []).filter(
    (row) =>
      (!roleFilter || row.roleId === roleFilter) &&
      (!statusFilter || (row.blockedAt ? 'BLOCKED' : row.status) === statusFilter) &&
      (!term ||
        `${row.login} ${roleRows.find((role) => role.id === row.roleId)?.name || ''}`
          .toLocaleLowerCase('ru')
          .includes(term)),
  );
  const inviteRows = (invites.data?.items || []).filter(
    (row) =>
      (!statusFilter || row.status === statusFilter) &&
      (!term || row.login.toLocaleLowerCase('ru').includes(term)),
  );
  const shownRoles = roleRows.filter(
    (role) =>
      !term ||
      `${role.name} ${role.rules.map((rule) => actionLabels[rule.action]).join(' ')}`
        .toLocaleLowerCase('ru')
        .includes(term),
  );
  const count = isRoles
    ? roleRows.length
    : isInvites
      ? invites.data?.items.length || 0
      : members.data?.items.length || 0;
  return (
    <AdministrativeList
      count={count}
      toolbar={
        <>
          <Search
            value={search}
            onChange={setSearch}
            placeholder={
              isRoles
                ? 'Поиск по названию роли'
                : isInvites
                  ? 'Поиск приглашений'
                  : 'Поиск по сотруднику или роли'
            }
          />
          <CompactFilter
            label={isRoles ? 'Роли' : isInvites ? 'Приглашения' : 'Участники'}
            allLabel="Перейти в раздел"
            value={section}
            options={[
              { id: 'members', name: 'Участники' },
              { id: 'roles', name: 'Роли и права' },
              { id: 'invites', name: 'Приглашения' },
            ]}
            onChange={(value) => {
              if (value) {
                setSearch('');
                setStatusFilter('');
                setRoleFilter('');
                void navigate(value);
              }
            }}
          />
          {!isRoles && (
            <CompactFilter
              label="Статус"
              allLabel="Все статусы"
              value={statusFilter}
              options={Object.entries(statusLabels).map(([id, name]) => ({ id, name }))}
              onChange={setStatusFilter}
            />
          )}
          {!isRoles && !isInvites && (
            <CompactFilter
              label="Роль"
              allLabel="Все роли"
              value={roleFilter}
              options={roleRows}
              onChange={setRoleFilter}
            />
          )}
          <span className="toolbar-spacer" />
          {me.allowedActions.includes('members.edit') && !isRoles && (
            <AdminLink path="invites/new" className="pill">
              <Icon name="Plus" size={16} /> Пригласить сотрудника
            </AdminLink>
          )}
          {me.allowedActions.includes('roles.edit') && isRoles && (
            <AdminLink path="roles/new" className="pill">
              <Icon name="Plus" size={16} /> Создать роль
            </AdminLink>
          )}
        </>
      }
    >
      {isRoles ? (
        <RoleList roles={shownRoles} />
      ) : isInvites ? (
        <InviteList invites={inviteRows} />
      ) : (
        <MemberList members={memberRows} roles={roleRows} />
      )}
    </AdministrativeList>
  );
}

function MemberList({ members, roles }: { members: Member[]; roles: Role[] }) {
  const me = useAdministrative();
  return (
    <Table
      rows={members}
      columns={[
        { key: 'login', label: 'Сотрудник', render: (row) => row.login },
        {
          key: 'roleId',
          label: 'Роль',
          render: (row) => roles.find((r) => r.id === row.roleId)?.name || 'Роль недоступна',
        },
        {
          key: 'status',
          label: 'Доступ',
          render: (row) =>
            row.blockedAt
              ? 'Административный доступ заблокирован'
              : statusLabels[row.status] || row.status,
        },
        {
          key: 'actions',
          label: '',
          sortable: false,
          render: (row) =>
            row.allowedActions.includes('members.edit') && row.accountId !== me.accountId ? (
              <AdminLink path={`members/${row.id}/edit`}>Изменить доступ</AdminLink>
            ) : row.accountId === me.accountId ? (
              <span className="muted">Вы</span>
            ) : null,
        },
      ]}
    />
  );
}
function MemberForm({
  member,
  roles,
  reload,
}: {
  member: Member;
  roles: Role[];
  reload: () => Promise<Member | undefined>;
}) {
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const me = useAdministrative();
  const [snapshot, setSnapshot] = useState(member);
  const [roleId, setRole] = useState(member.roleId);
  const [blocked, setBlocked] = useState(Boolean(member.blockedAt));
  const [confirm, setConfirm] = useState(false);
  const [reloading, setReloading] = useState(false);
  if (!member.allowedActions.includes('members.edit') || member.accountId === me.accountId)
    return (
      <Empty
        title="Изменение доступа недоступно"
        subtitle="Собственную роль изменяет другой администратор."
      />
    );
  const save = async () => {
    const result = await action.run<Member>(
      'PATCH',
      '/api/v1/administrative/members/{id}',
      { version: snapshot.version, roleId, blocked },
      { id: snapshot.id },
    );
    if (result) await navigate('members');
  };
  return (
    <>
      <AdminForm
        title="Изменить доступ сотрудника"
        action={{ ...action, pending: action.pending || reloading }}
        cancel="members"
        submit={async () => {
          if (blocked && !snapshot.blockedAt) setConfirm(true);
          else await save();
        }}
      >
        {(member.version !== snapshot.version ||
          (action.error instanceof ApiError && action.error.status === 409)) && (
          <div role="status">
            <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
            <Button
              disabled={action.pending || reloading}
              onClick={async () => {
                setReloading(true);
                try {
                  const latest = await reload();
                  if (!latest) return;
                  setSnapshot(latest);
                  setRole(latest.roleId);
                  setBlocked(Boolean(latest.blockedAt));
                  setConfirm(false);
                  action.setError(null);
                } finally {
                  setReloading(false);
                }
              }}
            >
              Загрузить новую версию
            </Button>
          </div>
        )}
        <InfoRows
          rows={[
            ['Сотрудник', snapshot.login],
            ['Текущий статус', statusLabels[snapshot.status] || snapshot.status],
          ]}
        />
        <AdminSelect
          label="Административная роль"
          options={roles}
          value={roleId}
          onChange={setRole}
          required
        />
        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={blocked}
            onChange={(event) => setBlocked(event.target.checked)}
          />{' '}
          Заблокировать административный доступ
        </label>
        <p className="muted">
          При блокировке активные сессии будут отозваны. Назначения и права в HR сохраняются.
        </p>
      </AdminForm>
      {confirm && (
        <AdminConfirm
          title="Заблокировать доступ?"
          action={action}
          onClose={() => setConfirm(false)}
          onConfirm={save}
          label="Заблокировать"
        >
          <p>
            {snapshot.login} потеряет доступ к административной базе. Активные сессии будут
            завершены.
          </p>
        </AdminConfirm>
      )}
    </>
  );
}
function InviteList({ invites }: { invites: Invite[] }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const [revoke, setRevoke] = useState<Invite | null>(null);
  return (
    <>
      <Table
        rows={invites}
        columns={[
          { key: 'login', label: 'Сотрудник' },
          {
            key: 'status',
            label: 'Состояние',
            render: (row) =>
              row.revokedAt
                ? 'Отозвано'
                : row.status === 'ACTIVE'
                  ? 'Активировано'
                  : new Date(row.expiresAt) < new Date()
                    ? 'Срок истёк'
                    : 'Ожидает активации',
          },
          {
            key: 'expiresAt',
            label: 'Действует до',
            render: (row) => dateTimeLabel(row.expiresAt, me.timezone),
          },
          {
            key: 'actions',
            label: '',
            sortable: false,
            render: (row) =>
              !row.revokedAt &&
              row.status === 'PENDING_ACTIVATION' &&
              row.allowedActions.includes('members.edit') ? (
                <Button onClick={() => setRevoke(row)}>Отозвать</Button>
              ) : null,
          },
        ]}
      />
      {revoke && (
        <AdminConfirm
          title="Отозвать приглашение?"
          action={action}
          onClose={() => setRevoke(null)}
          onConfirm={async () => {
            if (
              await action.run(
                'POST',
                '/api/v1/administrative/invites/{id}/revoke',
                { version: revoke.version },
                { id: revoke.id },
                'Приглашение отозвано',
              )
            )
              setRevoke(null);
          }}
          label="Отозвать"
        >
          <p>Одноразовая ссылка для {revoke.login} перестанет действовать.</p>
        </AdminConfirm>
      )}
    </>
  );
}
function InviteForm({ roles }: { roles: Role[] }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const [roleId, setRole] = useState(roles.find((row) => row.name === 'Сотрудник')?.id || '');
  const [invite, setInvite] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);
  if (!me.allowedActions.includes('members.edit'))
    return <Empty title="Создание приглашений недоступно" />;
  if (invite)
    return (
      <section className="form-page">
        <h1>{invite.existingAccount ? 'Доступ назначен' : 'Приглашение создано'}</h1>
        <div className="panel">
          <InfoRows
            rows={[
              ['Сотрудник', invite.login],
              ['Действует до', dateTimeLabel(invite.expiresAt, me.timezone)],
            ]}
          />
          {invite.actionLink ? (
            <>
              <p>
                Передайте сотруднику одноразовую ссылку. После активации он задаст пароль и будет
                работать под собственным аккаунтом.
              </p>
              <Field label="Ссылка приглашения" readOnly value={invite.actionLink} />
              <Button
                onClick={() => {
                  if (!navigator.clipboard) {
                    action.setError(new Error('Копирование недоступно. Выделите ссылку вручную.'));
                    return;
                  }
                  void navigator.clipboard
                    .writeText(invite.actionLink!)
                    .then(() => setCopied(true))
                    .catch(() =>
                      action.setError(
                        new Error('Не удалось скопировать. Выделите ссылку вручную.'),
                      ),
                    );
                }}
              >
                {copied ? 'Скопировано' : 'Скопировать ссылку'}
              </Button>
            </>
          ) : (
            <p>
              Сотрудник уже имеет активный аккаунт. Административное пространство доступно с его
              действующим паролем.
            </p>
          )}
          {action.error && <ErrorState error={action.error} />}
          <div className="admin-toolbar">
            <AdminLink path="members" className="pill primary">
              К участникам
            </AdminLink>
            <Button
              onClick={() => {
                setInvite(null);
                setCopied(false);
              }}
            >
              Ещё приглашение
            </Button>
          </div>
        </div>
      </section>
    );
  return (
    <AdminForm
      title="Пригласить сотрудника"
      action={action}
      cancel="members"
      label="Создать приглашение"
      submit={async (data) => {
        const body: Schema<'AdminInviteCreate'> = { login: text(data, 'login'), roleId };
        const created = await action.run<Invite>('POST', '/api/v1/administrative/invites', body);
        if (created) setInvite(created);
      }}
    >
      <Field label="Рабочий email" name="login" type="email" required autoComplete="off" />
      <AdminSelect label="Роль" options={roles} value={roleId} onChange={setRole} required />
      <p className="muted">
        Доступ выдаётся только к административной базе. Письма не отправляются. Если аккаунт уже
        активен, его пароль сохраняется.
      </p>
    </AdminForm>
  );
}
function RoleList({ roles }: { roles: Role[] }) {
  return (
    <Table
      rows={roles}
      columns={[
        { key: 'name', label: 'Роль' },
        {
          key: 'rules',
          label: 'Права',
          render: (role) =>
            `${role.rules.filter((rule) => rule.effect === 'ALLOW').length} разрешений, ${role.rules.filter((rule) => rule.effect === 'DENY').length} запретов`,
        },
        {
          key: 'actions',
          label: '',
          sortable: false,
          render: (role) =>
            role.allowedActions.includes('roles.edit') ? (
              <AdminLink path={`roles/${role.id}/edit`}>Настроить права</AdminLink>
            ) : null,
        },
      ]}
    />
  );
}
function RoleForm({ role, reload }: { role?: Role; reload: () => Promise<Role | undefined> }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const resources = useAdminList<Schema<'AdminResource'>>('/api/v1/administrative/resources', {
    state: 'ALL',
  });
  const [snapshot, setSnapshot] = useState(role);
  const [name, setName] = useState(role?.name || '');
  const ruleRows = (value?: Role) =>
    value?.rules.map((rule) => ({
      key: crypto.randomUUID(),
      rule: {
        action: rule.action,
        effect: rule.effect,
        scope: rule.scope,
        targetId: rule.targetId,
        ownOnly: rule.ownOnly,
      },
    })) || [];
  const [rows, setRows] = useState<{ key: string; rule: Rule }[]>(() => ruleRows(role));
  const [remove, setRemove] = useState(false);
  const [reloading, setReloading] = useState(false);
  if (
    !me.allowedActions.includes('roles.edit') ||
    (role && !role.allowedActions.includes('roles.edit'))
  )
    return <Empty title="Настройка ролей недоступна" />;
  const update = (key: string, patch: Partial<Rule>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, rule: { ...row.rule, ...patch } } : row)),
    );
  const options = (scope: Rule['scope']) =>
    scope === 'SECTION'
      ? me.sections
      : scope === 'SHEET'
        ? me.sheets.map((sheet) => ({
            id: sheet.id,
            name: `${me.sections.find((section) => section.id === sheet.sectionId)?.name || 'Раздел'} / ${sheet.name}`,
          }))
        : resources.items;
  return (
    <>
      <AdminForm
        title={snapshot ? 'Настроить роль' : 'Создать роль'}
        action={{ ...action, pending: action.pending || reloading }}
        cancel="roles"
        submit={async (data) => {
          const body = {
            name: text(data, 'name'),
            rules: rows.map((row) => row.rule),
            ...(snapshot ? { version: snapshot.version } : {}),
          };
          const result = await action.run<Role>(
            snapshot ? 'PATCH' : 'POST',
            snapshot ? '/api/v1/administrative/roles/{id}' : '/api/v1/administrative/roles',
            body,
            snapshot ? { id: snapshot.id } : {},
          );
          if (result) await navigate('roles');
        }}
      >
        {role &&
          (role.version !== snapshot?.version ||
            (action.error instanceof ApiError && action.error.status === 409)) && (
            <div role="status">
              <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
              <Button
                disabled={action.pending || reloading}
                onClick={async () => {
                  setReloading(true);
                  try {
                    const latest = await reload();
                    if (!latest) return;
                    setSnapshot(latest);
                    setName(latest.name);
                    setRows(ruleRows(latest));
                    setRemove(false);
                    action.setError(null);
                  } finally {
                    setReloading(false);
                  }
                }}
              >
                Загрузить новую версию
              </Button>
            </div>
          )}
        <Field
          label="Название роли"
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={200}
        />
        <h2>Разрешения</h2>
        <p className="muted">
          По умолчанию действие запрещено. Явный запрет на пространстве, разделе, листе или записи
          перекрывает разрешение. Просмотр карточки отдельно от просмотра финансов и паролей.
        </p>
        {rows.map(({ key, rule }, index) => (
          <div className="panel admin-rule-editor" key={key}>
            <h3>Правило {index + 1}</h3>
            <AdminSelect
              label="Действие"
              value={rule.action}
              onChange={(value) =>
                update(key, {
                  action: value as Rule['action'],
                  ownOnly: allowsOwnRecords(value) ? rule.ownOnly : false,
                })
              }
              options={Object.entries(actionLabels).map(([id, name]) => ({ id, name }))}
              required
            />
            <AdminSelect
              label="Разрешение"
              value={rule.effect}
              onChange={(value) => update(key, { effect: value as Rule['effect'] })}
              options={[
                { id: 'ALLOW', name: 'Разрешить' },
                { id: 'DENY', name: 'Запретить' },
              ]}
              required
            />
            <AdminSelect
              label="Область"
              value={rule.scope}
              onChange={(value) => update(key, { scope: value as Rule['scope'], targetId: null })}
              options={Object.entries(scopeLabels).map(([id, name]) => ({ id, name }))}
              required
            />
            {rule.scope !== 'WORKSPACE' && (
              <AdminSelect
                label="Объект"
                value={rule.targetId || ''}
                onChange={(targetId) => update(key, { targetId })}
                options={
                  rule.targetId && !options(rule.scope).some((item) => item.id === rule.targetId)
                    ? [
                        ...options(rule.scope),
                        { id: rule.targetId, name: 'Назначенный объект недоступен' },
                      ]
                    : options(rule.scope)
                }
                required
              />
            )}
            {allowsOwnRecords(rule.action) && (
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={rule.ownOnly}
                  onChange={(event) => update(key, { ownOnly: event.target.checked })}
                />{' '}
                Только записи, созданные сотрудником
              </label>
            )}
            <Button onClick={() => setRows((current) => current.filter((row) => row.key !== key))}>
              Убрать правило
            </Button>
          </div>
        ))}
        <Button
          disabled={rows.length >= 500}
          onClick={() =>
            setRows((current) => [
              ...current,
              {
                key: crypto.randomUUID(),
                rule: {
                  action: 'resource.read',
                  effect: 'ALLOW',
                  scope: 'WORKSPACE',
                  targetId: null,
                  ownOnly: false,
                },
              },
            ])
          }
        >
          Добавить правило
        </Button>
        {resources.hasNextPage && (
          <Button
            disabled={resources.isFetchingNextPage}
            onClick={() => void resources.fetchNextPage()}
          >
            Загрузить ещё записи для выбора
          </Button>
        )}
        {resources.error && <ErrorState error={resources.error} />}
      </AdminForm>
      {snapshot && (
        <div className="admin-separated">
          <Button onClick={() => setRemove(true)}>Удалить роль</Button>
        </div>
      )}
      {remove && snapshot && (
        <AdminConfirm
          title="Удалить роль?"
          action={action}
          onClose={() => setRemove(false)}
          onConfirm={async () => {
            if (
              await action.run(
                'DELETE',
                '/api/v1/administrative/roles/{id}',
                { version: snapshot.version },
                { id: snapshot.id },
                'Роль удалена',
              )
            )
              await navigate('roles');
          }}
        >
          <p>
            Роль можно удалить после назначения её участникам другой роли. Права HR сохраняются.
          </p>
        </AdminConfirm>
      )}
    </>
  );
}
